<template>
  <div class="panel">
    <div class="panel-title">Bloquer / débloquer une société</div>
    <div class="panel-subtitle">
      Met à jour le champ <code>hitonline.company.blocked</code>.
    </div>

    <div class="panel-grid">
      <hit-select
        class="min-w-[32rem] w-full"
        :label="'Société'"
        :options="companyOptions"
        :value="selectedSchemaId"
        item-id-property="id"
        :label-fields="['label']"
        clearable
        @change="onCompanyChange"
      />

      <hit-select
        class="min-w-[20rem] w-full"
        :label="'Action'"
        :options="actionOptions"
        :value="selectedAction"
        item-id-property="id"
        :label-fields="['label']"
        clearable="false"
        :disabled="
          !selectedSchemaId || currentStatusLoading || currentBlocked === null
        "
        @change="onActionChange"
      />

      <div v-if="selectedSchemaId" class="status-cell">
        <div v-if="currentStatusLoading" class="status-pill">
          <span class="dot dot-loading" />
          Statut actuel: chargement…
        </div>
        <div
          v-else-if="currentBlocked !== null"
          class="status-pill"
          :class="currentBlocked ? 'status-pill-blocked' : 'status-pill-ok'"
        >
          <span class="dot" :class="currentBlocked ? 'dot-danger' : 'dot-ok'" />
          <span class="status-label">Statut actuel</span>
          <span class="status-sep">•</span>
          <span class="status-value">{{
            currentBlocked ? 'Bloquée' : 'Non bloquée'
          }}</span>
        </div>
        <div v-else class="status-pill status-pill-warn">
          <span class="dot dot-warn" />
          Statut actuel: indisponible
        </div>
      </div>
    </div>

    <div
      v-if="selectedSchemaId && currentBlocked !== null"
      class="mt-4 info-panel"
    >
      <div class="info-title">
        Action proposée:
        <strong>{{
          selectedAction === 'UNBLOCK' ? 'Débloquer' : 'Bloquer'
        }}</strong>
      </div>
      <div class="info-text">
        {{
          currentBlocked
            ? 'La société est actuellement bloquée. Tu peux la débloquer pour réactiver l’accès.'
            : 'La société n’est pas bloquée. Tu peux la bloquer pour empêcher l’accès.'
        }}
      </div>
    </div>

    <div class="mt-4 flex gap-2">
      <hit-button
        :label="runLabel"
        color="accent"
        :prefix-icon="runIcon"
        :disabled="running || !canRun"
        @click="run"
      />
      <hit-button
        :label="'Réinitialiser'"
        color="button"
        prefix-icon="reset_changes"
        :disabled="running"
        @click="resetForm"
      />
    </div>

    <div v-if="result" class="result mt-4">
      <div class="result-title" :class="{ok: result.ok, ko: !result.ok}">
        {{ result.ok ? 'Succès' : 'Erreur' }}
      </div>
      <pre class="result-pre">{{ resultText }}</pre>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitButton, HitSelect} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';

export default defineComponent({
  name: 'ScriptBlockCompany',
  components: {HitButton, HitSelect},
  data() {
    return {
      companyOptions: [],
      selectedSchemaId: null,
      currentStatusLoading: false,
      currentBlocked: null,
      selectedAction: null,
      running: false,
      result: null,
    };
  },
  computed: {
    actionOptions() {
      return [
        {id: 'BLOCK', label: 'Bloquer'},
        {id: 'UNBLOCK', label: 'Débloquer'},
      ];
    },
    canRun() {
      return (
        !!this.selectedSchemaId &&
        !!this.selectedAction &&
        this.currentBlocked !== null
      );
    },
    desiredBlockedValue() {
      return this.selectedAction === 'BLOCK';
    },
    runLabel() {
      if (this.selectedAction === 'UNBLOCK') return 'Débloquer la société';
      return 'Bloquer la société';
    },
    runIcon() {
      return this.selectedAction === 'UNBLOCK' ? 'lock_open' : 'lock';
    },
    resultText() {
      try {
        return JSON.stringify(this.result, null, 2);
      } catch {
        return String(this.result);
      }
    },
  },
  async mounted() {
    await this.loadCompanyOptions();
  },
  methods: {
    async loadCompanyOptions() {
      try {
        const res = await adminApi.listCompanyOptions({limit: 500});
        const rows = res?.rows || [];
        this.companyOptions = rows.map((r) => {
          const label = [r.designation, r.vat_no].filter(Boolean).join(' – ');
          return {id: r.schema_id || r.id, label: label || r.id};
        });
      } catch {
        this.companyOptions = [];
      }
    },
    onCompanyChange(value) {
      this.selectedSchemaId = value;
      this.result = null;
      this.currentBlocked = null;
      this.selectedAction = null;
      this.fetchCurrentStatus();
    },
    onActionChange(value) {
      this.selectedAction = value;
      this.result = null;
    },
    resetForm() {
      this.selectedSchemaId = null;
      this.currentStatusLoading = false;
      this.currentBlocked = null;
      this.selectedAction = null;
      this.result = null;
    },
    async fetchCurrentStatus() {
      const schemaId = this.selectedSchemaId;
      if (!schemaId) {
        this.currentBlocked = null;
        this.currentStatusLoading = false;
        return;
      }

      this.currentStatusLoading = true;
      this.currentBlocked = null;
      try {
        const res = await adminApi.getCompanyBlockedStatus(schemaId);
        const blocked = res?.details?.blocked;
        if (this.selectedSchemaId === schemaId) {
          this.currentBlocked = typeof blocked === 'boolean' ? blocked : null;
          // Default action is the inverse of the current status
          if (typeof blocked === 'boolean') {
            this.selectedAction = blocked ? 'UNBLOCK' : 'BLOCK';
          } else {
            this.selectedAction = null;
          }
        }
      } catch {
        if (this.selectedSchemaId === schemaId) {
          this.currentBlocked = null;
          this.selectedAction = null;
        }
      } finally {
        if (this.selectedSchemaId === schemaId) {
          this.currentStatusLoading = false;
        }
      }
    },
    async run() {
      if (!this.canRun) return;
      this.running = true;
      this.result = null;
      try {
        const res = await adminApi.runScript('/scripts/block_company/run', {
          schema_id: this.selectedSchemaId,
          blocked: this.desiredBlockedValue,
        });
        this.result = res;
        await this.fetchCurrentStatus();
      } catch (e) {
        this.result = {ok: false, message: e?.message || String(e)};
      } finally {
        this.running = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.panel {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.82);
  padding: 0.9rem;
}

.panel-title {
  font-weight: 700;
  color: rgba(15, 23, 42, 0.92);
  margin-bottom: 0.15rem;
}

.panel-subtitle {
  color: rgba(100, 116, 139, 1);
  font-size: 0.9rem;
  line-height: 1.25rem;
  margin-bottom: 0.85rem;
}

.panel-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.status-cell {
  flex: 1 1 100%;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(248, 250, 252, 1);
  color: rgba(15, 23, 42, 0.9);
  font-size: 0.82rem;
  white-space: nowrap;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  display: inline-block;
  box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.08);
}
.dot-ok {
  background: rgba(16, 185, 129, 1);
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18);
}
.dot-danger {
  background: rgba(239, 68, 68, 1);
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18);
}
.dot-warn {
  background: rgba(249, 115, 22, 1);
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.18);
}
.dot-loading {
  background: rgba(148, 163, 184, 1);
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.18);
}

.status-label {
  opacity: 0.92;
}
.status-sep {
  opacity: 0.6;
}

.status-pill-ok {
  border-color: rgba(16, 185, 129, 0.9);
  background: color-mix(in srgb, var(--color-accent, #059669) 82%, #022c22);
  color: rgba(255, 255, 255, 0.96);
  box-shadow: 0 4px 10px rgba(5, 150, 105, 0.3);
}

.status-pill-blocked {
  border-color: rgba(239, 68, 68, 0.35);
  background: rgba(254, 242, 242, 1);
  color: rgba(153, 27, 27, 1);
  box-shadow: 0 1px 3px rgba(239, 68, 68, 0.18);
}

.status-value {
  font-weight: 800;
  letter-spacing: 0.02em;
}

.status-pill-warn {
  color: rgba(180, 83, 9, 1);
  border-color: rgba(249, 115, 22, 0.25);
  background: rgba(255, 247, 237, 1);
  box-shadow: 0 1px 3px rgba(249, 115, 22, 0.25);
}

.info-panel {
  padding: 0.7rem 0.85rem;
  border-radius: 0.85rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(248, 250, 252, 0.96);
  display: grid;
  gap: 0.25rem;
}

.info-title {
  font-weight: 700;
  color: rgba(15, 23, 42, 0.92);
}

.info-text {
  color: rgba(15, 23, 42, 0.75);
  font-size: 0.88rem;
  line-height: 1.25rem;
}

.result-title {
  font-weight: 700;
}
.result-title.ok {
  color: #03695a;
}
.result-title.ko {
  color: #b91c1c;
}
.result-pre {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  line-height: 1.15rem;
  background: rgba(248, 250, 252, 1);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.65rem;
  padding: 0.6rem 0.65rem;
  max-height: 18rem;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
